import React from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { l10n } from '@ott/l10n';
import { useLayoutContext } from '@ott/layout-context';
import PlusMinus from '@ott/plus-minus';
import OTTTravellers, { TravellersItem } from '@ott/travellers';
import TravellersChildren from '@ott/travellers-children';

import { FIELDS } from '../../constants';
import { SearchForm } from '../../types';

import styles from './Travellers.scss';

const MIN_ADULTS = 1;
export const MAX_TRAVELLERS = 4;
export const CHILDREN_AGE_RANGE = {
  start: 0,
  end: 12,
};

const Travellers = () => {
  const { control } = useFormContext<SearchForm>();
  const { field: adultsField } = useController({ name: FIELDS.ADULTS, control, defaultValue: 1 });
  const { field: childrenAgesField } = useController({ name: FIELDS.CHILDREN_AGES, control });

  const adultsCount = adultsField.value;
  const childrenCount = childrenAgesField.value?.length || 0;

  const { isMobile, isDesktop } = useLayoutContext();

  const getAgeTitle = (age: number) => {
    if (!age) {
      return l10n('travellersControl.infantAge');
    }

    return l10n('travellersControl.childAge', { age });
  };

  const getAdultsTitle = (value: number) => {
    return `${value} ${l10n('adults', { value })}`;
  };

  const renderTravellers = () => {
    const remainder = MAX_TRAVELLERS - adultsCount - childrenCount;
    const adultsNode = (
      <PlusMinus
        min={MIN_ADULTS}
        max={adultsCount + remainder}
        value={adultsCount}
        label={getAdultsTitle(adultsCount)}
        onChange={adultsField.onChange}
      />
    );
    const childrenNode = (
      <TravellersChildren
        maxChildren={childrenCount + remainder}
        childrenAgeRange={CHILDREN_AGE_RANGE}
        childrenAges={childrenAgesField.value || []}
        onChildrenAgesChange={childrenAgesField.onChange}
        getAgeTitle={getAgeTitle}
      />
    );

    return <TravellersItem adultsNode={adultsNode} childrenNode={childrenNode} />;
  };

  const getTriggerValue = () => {
    if (!childrenCount) {
      return getAdultsTitle(adultsCount);
    }

    return l10n('searchForm.passengersLabel', {
      adults: adultsCount,
      children: childrenCount,
    });
  };

  return (
    <OTTTravellers
      childrenClassNames={{
        root: styles.travellers,
        input: styles.travellersTrigger,
        container: styles.travellersContainer,
      }}
      triggerValue={getTriggerValue()}
      travellersList={renderTravellers()}
      hasDoneButton={isMobile}
      withArrowIcon={true}
      isAbsoluteOverlayPosition={isDesktop}
    />
  );
};

export default React.memo(Travellers);
